// [S-061] Certified template detail. Direct port of
// docs/Wireframes/Template.html with three intentional choices:
//
//   1. Document body (`td__doc`) renders the actual `content` string in
//      Source Serif via the design-system token. The wireframe's
//      hand-typed §4.2 / §7.1 / §12.3 sections are placeholders for
//      whatever the seed/Stage-2 ingestion produces; we render whatever
//      the API returns so a re-attestation that updates content is
//      visible immediately.
//   2. Version history shows ALL FIVE lifecycle states; the current
//      state is highlighted. We don't yet have a per-id revision feed
//      from the backend (no story has shipped that), so each state
//      shows where the *current record* sits in the FSM rather than a
//      list of historical versions. A follow-up to expose the prior
//      template via `successorId` traversal would change this to a
//      richer timeline.
//   3. "Review Contract against this template" CTA is a stub that
//      navigates to /review (NotYetBuilt → S-061a) carrying the
//      template id in the path. S-061a wires it.
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { diffWordsWithSpace, type Change } from "diff";
import {
  describeVariablesOutcome,
  useAdminTemplate,
  useCloneTemplateAsDraft,
  useExportTemplate,
  useRecordPeerReview,
  useTemplatePermalink,
  type CertifiedTemplateRow,
  type TaxonomyCoverageRow,
} from "../data/templatesQuery";
import { ApiError } from "../data/api";
import { Alert, Button, EditorFrame, LoadingState, PaginatedDocument } from "../components";
import { AssignPeerReviewerModal } from "../components/templates/TemplateLifecycleModals";
import { EditTagsModal } from "../components/templates/EditTagsModal";
import { useVariableMarks } from "../components/templates/useVariableMarks";
import { withoutVariableMarks } from "../components/templates/variableHighlight";
import { slotsFromMarkerBoundaries } from "../components/templates/variableMarkers";
import { useSession } from "../auth/AuthProvider";
import { sessionCan } from "../auth/capabilities";
import { talentTierLabel } from "../lib/talentTier";
import { useActorLabels } from "../data/identitiesQuery";
import { useTemplateVariablesApproved } from "../data/templateVariablesQuery";

// [S-640] Honest status pill per lifecycle state — the hero no longer claims
// "✓ Certified" for a draft/deprecated template.
// [S-672] "deprecated" displays as "Retired" (Bill: the word "deprecated" is
// ambiguous jargon) — the underlying lifecycleState value is unchanged.
const STATUS_PILL: Record<
  CertifiedTemplateRow["lifecycleState"],
  { label: string; cls: string }
> = {
  draft: { label: "Draft", cls: "td__pill--draft" },
  certified: { label: "✓ Certified", cls: "td__pill--cert" },
  re_certified: { label: "✓ Re-certified", cls: "td__pill--cert" },
  under_review: { label: "Under review", cls: "td__pill--review" },
  deprecated: { label: "Retired", cls: "td__pill--dep" },
};

const LIFECYCLE_STATES = [
  { state: "draft", label: "Draft", hint: "Submitted, awaiting attorney review" },
  { state: "certified", label: "Certified", hint: "Attorney-approved, in active use" },
  {
    state: "under_review",
    label: "Under review",
    hint: "Flagged for re-approval against new CBA / regulation",
  },
  {
    state: "re_certified",
    label: "Re-certified",
    hint: "Re-approved against the current CBA",
  },
  {
    state: "deprecated",
    label: "Retired",
    hint: "Superseded — see successor template",
  },
] as const;

// [S-413] Optional templateId lets the Studio Agents drawer embed this view by
// id; with no prop it reads the route param as before. `embedded` (S-413/S-511)
// renders a focused document-only view for the drawer — hero + the formatted
// document body, without the side rail (approval card, version history,
// actions), which belongs on the full-page route ("open in new tab").
export function TemplateDetail({
  templateId: templateIdProp,
  embedded = false,
}: {
  templateId?: string;
  embedded?: boolean;
} = {}): JSX.Element {
  const params = useParams<{ templateId: string }>();
  const templateId = templateIdProp ?? params.templateId;
  const session = useSession();
  // tenantId enables the L2 fallback inside useTemplateDetail when the
  // template id doesn't resolve in /layer1/templates.
  const tenantId =
    session?.studioId !== null && session?.studioId !== undefined
      ? `tenant-${session.studioId}`
      : "";
  // [draft permalink] An author's permalink resolves EVERY lifecycle state —
  // the hero pill, version history and peer-review card already render all
  // five honestly. Readers keep the old certified-only not-found. See
  // useTemplatePermalink for why a draft used to 404 here.
  const canAuthorTemplates =
    sessionCan(session, "template:author", { layer: "L1" }) ||
    sessionCan(session, "template:author", { layer: "L2" });
  const { data, isLoading, isError, error } = useTemplatePermalink(
    templateId,
    tenantId,
    canAuthorTemplates
  );
  const navigate = useNavigate();

  // [S-681 revision] The right panel (Approval / Version history / Quick
  // actions) collapses as ONE unit — same pattern as the app shell's left
  // sidebar (S-399, AppShell.tsx): a persisted boolean + a grid-column
  // collapse, not per-card toggles.
  const [sideCollapsed, setSideCollapsed] = useState<boolean>(
    () => localStorage.getItem("cs.templateSidePanelCollapsed") === "1"
  );
  useEffect(() => {
    localStorage.setItem(
      "cs.templateSidePanelCollapsed",
      sideCollapsed ? "1" : "0"
    );
  }, [sideCollapsed]);

  if (isLoading) {
    return (
      <div className="tpl__state" role="status" aria-live="polite">
        Loading template…
      </div>
    );
  }

  if (isError) {
    const isNotFound = error instanceof ApiError && error.status === 404;
    return (
      <Alert
        label={isNotFound ? "Template not found" : "Couldn't load template"}
        tone="error"
      >
        {isNotFound ? (
          <>
            No certified template with id <code>{templateId}</code>.{" "}
            <Link to="/library">Back to the certified templates</Link>
          </>
        ) : (
          (error?.message ?? "The admin API returned an error.")
        )}
      </Alert>
    );
  }

  if (data === undefined) return <></>;

  // [S-640] Trust signals must be HONEST. The status pill reflects the ACTUAL
  // lifecycle state (a deprecated/draft template no longer reads "✓ Certified"),
  // and the CBA anchor shows the REAL `anchorCBA` field — previously it was a
  // label synthesized from guild+year, so an un-anchored (placeholder) template
  // looked identically "anchored" to a properly certified one (Bill's concern).
  const cba = data.anchorCBA?.trim();
  const anchored = cba !== undefined && cba.length > 0;
  const state = data.lifecycleState;
  const statusPill = STATUS_PILL[state];

  const hero = (
    <div className="td__hero">
      <div className="td__hero__id">
        {data.layerSource === "L1" ? "L1 (platform)" : "L2 (studio)"} · effective{" "}
        {formatDate(data.effectiveFrom)}
      </div>
      <div className="td__hero__title">{prettyTitle(data)}</div>
      <div className="td__hero__row">
        <span className={`td__pill ${statusPill.cls}`}>{statusPill.label}</span>
        <span className="td__pill td__pill--ver">{prettyTier(data.tier)}</span>
        <span
          className={`td__pill ${
            data.documentForm == null ? "td__pill--unanchored" : "td__pill--form"
          }`}
          data-testid="td-document-form"
        >
          {prettyDocumentForm(data.documentForm)}
        </span>
        {anchored ? (
          <span className="td__pill td__pill--cba">Anchored to {cba}</span>
        ) : (
          <span
            className="td__pill td__pill--unanchored"
            title="This template isn't linked to a specific collective bargaining agreement."
            data-testid="td-unanchored"
          >
            Not anchored to a CBA
          </span>
        )}
      </div>
    </div>
  );

  // [rail epic] Drawer/rail view: hero + the READ-ONLY document only. A
  // template opened this way is almost always a citation the agent
  // surfaced mid-conversation (e.g. while drafting a deal) — editing a
  // certified template is a deliberate act that goes through the
  // certification-adjacent Template Management workflow, not something to
  // fall into from a chat citation. No edit toggle, no side rail (approval
  // card, version history, quick actions) — those live on the full route.
  if (embedded) {
    return (
      <div className="td__embedded">
        {hero}
        <TokenizedDocument content={data.content} />
      </div>
    );
  }

  return (
    <>
      {/* [S-681 revision] Toggle lives OUTSIDE .td__split so it stays
          reachable in both states — putting it inside .td__side would hide
          the only way to re-expand once collapsed, same reasoning as the
          app shell's sidebar toggle living in TopBar rather than in .sb. */}
      <div className="td__panel-bar">
        <Button
          variant="ghost"
          className="btn--s td__side-toggle"
          aria-label={sideCollapsed ? "Show details panel" : "Hide details panel"}
          aria-expanded={!sideCollapsed}
          aria-controls="td-side-panel"
          title={sideCollapsed ? "Show details panel" : "Hide details panel"}
          onClick={() => setSideCollapsed((v) => !v)}
          data-testid="td-side-toggle"
        >
          <span
            className={`td__side-toggle__chevron${
              sideCollapsed ? " td__side-toggle__chevron--collapsed" : ""
            }`}
            aria-hidden="true"
          />
          {sideCollapsed ? "Show details" : "Hide details"}
        </Button>
      </div>

      <div className={`td__split${sideCollapsed ? " td__split--collapsed" : ""}`}>
        <div className="td__main">
          {hero}

          {/* [draft permalink] A draft reached through its permalink needs the
              way back INTO the editor — this page's own edit toggle clones
              into a NEW draft, which is the right move for an immutable
              certified template and the wrong one for a draft still being
              written. */}
          {data.lifecycleState === "draft" && canAuthorTemplates && (
            <Alert label="This template is still a draft" spacedBottom>
              It hasn't been certified yet, so it isn't in the certified
              library.{" "}
              <Link to={`/library/${encodeURIComponent(data.id)}/edit`}>
                Continue editing
              </Link>
            </Alert>
          )}

          <EditableDocument template={data} tenantId={tenantId} />

          {data.successorId !== null && (
            <SuccessorDiffSection
              base={data}
              successorId={data.successorId}
              tenantId={tenantId}
            />
          )}

        </div>

        {/* Always rendered (not conditionally unmounted) so collapsing
            doesn't lose scroll position / re-fetch anything — hidden
            purely via CSS, same as .shell--collapsed .sb. */}
        <div className="td__side" id="td-side-panel">
          <AttestationCard template={data} />
          <TaxonomyCoverageCard coverage={data.taxonomyCoverage ?? null} />
          <VersionHistory currentState={data.lifecycleState} />
          <QuickActions
            template={data}
            onReview={() => navigate("/drafts")}
          />
        </div>
      </div>
    </>
  );
}

// [S-681] Approval / Version history / Quick actions each render their
// label row through this shared shell — kept as a component so the three
// cards share one place to change the header, not because it's collapsible
// itself. Collapsing (S-681 revision) applies to the WHOLE right panel at
// once, via TemplateDetail's sidebar toggle — see .td__side / .td__split.
function SectionCard({
  className,
  title,
  right,
  testId,
  children,
}: {
  className: string;
  title: string;
  right?: ReactNode;
  testId: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className={className} data-testid={testId}>
      <div className="td__attest__lbl">
        <span>{title}</span>
        {right}
      </div>
      {children}
    </div>
  );
}

function AttestationCard({
  template,
}: {
  template: CertifiedTemplateRow;
}): JSX.Element {
  // [S-640] Honest provenance: the seal + layer reflect the ACTUAL state, and
  // the anchor shows the REAL anchorCBA (flagging placeholders that have none).
  const isCertified =
    template.lifecycleState === "certified" ||
    template.lifecycleState === "re_certified";
  const cba = template.anchorCBA?.trim();
  const anchored = cba !== undefined && cba.length > 0;

  // Actor uids (submittedBy/peerReviewerUid) have no persisted profile — the
  // card never shows the raw uid, only the resolved name/email.
  const actorUids = [template.submittedBy];
  if (template.peerReviewerUid != null && template.peerReviewerUid.length > 0) {
    actorUids.push(template.peerReviewerUid);
  }
  const actorLabels = useActorLabels(actorUids, template.tenantId ?? "");

  const successorQuery = useAdminTemplate(
    template.successorId ?? undefined,
    template.tenantId ?? ""
  );

  return (
    <SectionCard
      className="td__attest"
      title="Approval"
      right={
        <span className="seal">
          {STATUS_PILL[template.lifecycleState].label.replace("✓ ", "")}
        </span>
      }
      testId="td-section-approval"
    >
      <div className="td__attest__row">
        <span className="k">Template</span>
        <span className="v">{prettyTitle(template)}</span>
      </div>
      <div className="td__attest__row">
        <span className="k">Layer</span>
        <span className="v">
          {template.layerSource === "L1" ? "L1 (platform)" : "L2 (studio)"}
        </span>
      </div>
      <div className="td__attest__row">
        <span className="k">{isCertified ? "Certified by" : "Submitted by"}</span>
        <span className="v">{actorLabels?.get(template.submittedBy) ?? "—"}</span>
      </div>
      <div className="td__attest__row">
        <span className="k">Anchored</span>
        <span className="v">
          {anchored ? (
            <b>{cba}</b>
          ) : (
            <span className="td__attest__unanchored">Not anchored to a CBA</span>
          )}
        </span>
      </div>
      <div className="td__attest__row">
        <span className="k">Effective</span>
        <span className="v">{formatDate(template.effectiveFrom)}</span>
      </div>
      <div className="td__attest__row">
        <span className="k">Expires</span>
        <span className="v">
          {template.effectiveTo === null ? "—" : formatDate(template.effectiveTo)}
        </span>
      </div>
      {template.successorId !== null && (
        <div className="td__attest__row">
          <span className="k">Successor</span>
          <span className="v">
            {successorQuery.data !== undefined
              ? prettyTitle(successorQuery.data)
              : "…"}
          </span>
        </div>
      )}
      {template.peerReviewerUid != null && template.peerReviewerUid.length > 0 && (
        <div className="td__attest__row" data-testid="td-peer-review-row">
          <span className="k">Peer review</span>
          <span className="v">
            {(() => {
              const reviewer = actorLabels?.get(template.peerReviewerUid) ?? "—";
              return template.peerReviewedAt != null &&
                template.peerReviewedAt.length > 0
                ? `${reviewer} on ${formatDate(template.peerReviewedAt)}`
                : `assigned to ${reviewer} (pending)`;
            })()}
          </span>
        </div>
      )}
    </SectionCard>
  );
}

// [Review Engine V2] What the clause_type taxonomy could name in this template
// when it was certified.
//
// This matters because the review engine aligns a draft against this template BY
// CLAUSE TYPE, and a template clause the taxonomy can't name is dropped before
// the engine sees it — it can never produce a "missing" finding, however badly a
// draft omits it. A low number here means this standard is quietly smaller than
// it looks.
//
// Renders NOTHING when the template was never measured. Null is not "fully
// covered": it means certified before the check existed, or the classifier was
// unreachable that day. Showing "—" or a 0% would both be lies (S-640: trust
// signals must be honest).
function TaxonomyCoverageCard({
  coverage,
}: {
  coverage: TaxonomyCoverageRow | null;
}): JSX.Element {
  if (coverage === null) return <></>;
  const { totalClauses, typedClauses, uncovered, lowConfidence, segmentation } =
    coverage;
  // A template that didn't parse into clauses has a SEGMENTATION problem, and
  // every "uncovered clause" under it is suspect. Say that instead of reporting
  // a taxonomy gap that may not exist.
  const unparsed = segmentation.method === "paragraph" && segmentation.confidence < 0.5;
  return (
    <SectionCard
      className="td__attest td__coverage"
      title="Taxonomy coverage"
      testId="td-section-coverage"
      right={
        uncovered.length > 0 ? (
          <span className="td__pill td__pill--unanchored">
            {uncovered.length} unnamed
          </span>
        ) : undefined
      }
    >
      <div className="td__attest__row">
        <span className="k">Clauses typed</span>
        <span className="v" data-testid="td-coverage-count">
          {typedClauses} of {totalClauses}
        </span>
      </div>
      {unparsed && (
        <p className="td__coverage__warn" data-testid="td-coverage-unparsed">
          This template didn't split into clauses cleanly, so the figures above
          describe the parse, not the taxonomy.
        </p>
      )}
      {uncovered.length > 0 && (
        <>
          <p className="td__coverage__hint">
            No clause type names these, so a draft can never be flagged for
            omitting them:
          </p>
          <ul className="td__coverage__list" data-testid="td-coverage-uncovered">
            {uncovered.map((c) => (
              <li key={c.index}>{c.heading}</li>
            ))}
          </ul>
        </>
      )}
      {lowConfidence.length > 0 && (
        <>
          {/* A separate defect with a separate fix: the clause_type EXISTS but
              its definition/discriminators don't separate it from a confusable
              sibling. Never summed with the count above — merging the two is how
              a taxonomy grows duplicates. */}
          <p className="td__coverage__hint">
            Typed, but not confidently — these clause types may need sharper
            definitions:
          </p>
          <ul className="td__coverage__list" data-testid="td-coverage-lowconf">
            {lowConfidence.map((c) => (
              <li key={c.index}>
                {c.heading} <span className="td__coverage__type">{c.clauseType}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      <div className="td__attest__row">
        <span className="k">Measured</span>
        <span className="v">
          {formatDate(coverage.checkedAt)}
          {coverage.catalogScope === "platform" ? " · platform catalog" : ""}
        </span>
      </div>
    </SectionCard>
  );
}

function VersionHistory({
  currentState,
}: {
  currentState: CertifiedTemplateRow["lifecycleState"];
}): JSX.Element {
  return (
    <SectionCard
      className="td__history"
      title="Version history"
      testId="td-section-history"
    >
      <div className="td__history__steps">
        {LIFECYCLE_STATES.map((step) => {
          const isCurrent = step.state === currentState;
          return (
            <div
              key={step.state}
              className={
                isCurrent
                  ? "td__history__step td__history__step--current"
                  : "td__history__step"
              }
              aria-current={isCurrent ? "step" : undefined}
            >
              <span className="td__history__step__dot" aria-hidden="true" />
              <div>
                <div className="td__history__step__name">{step.label}</div>
                <div className="td__history__step__hint">{step.hint}</div>
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

function QuickActions({
  template,
  onReview,
}: {
  template: CertifiedTemplateRow;
  onReview: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const session = useSession();
  const exportTemplate = useExportTemplate();
  const isExporting = exportTemplate.isPending;
  const exportError =
    exportTemplate.isError && exportTemplate.error instanceof ApiError
      ? exportTemplate.error.body
      : null;

  // [S-221 follow-up] Peer-review handoff actions. Surface depends on
  // caller identity vs the template's peerReviewerUid:
  //   - assigned peer (and not yet recorded): "Record peer review"
  //   - lead attestor (anyone else who's admin-of-layer): "Assign / Re-assign"
  // The assignment is only legal in draft / under_review — same gate
  // the server enforces. Outside those states we hide the buttons.
  const [assigningPeer, setAssigningPeer] = useState(false);
  // S-216 — retag an existing template. Open with the template object;
  // close clears.
  const [retagOpen, setRetagOpen] = useState(false);
  const recordPeer = useRecordPeerReview();
  const peerEligibleState =
    template.lifecycleState === "draft" ||
    template.lifecycleState === "under_review";
  const isAssignedPeer =
    session !== null &&
    template.peerReviewerUid != null &&
    template.peerReviewerUid.length > 0 &&
    session.uid === template.peerReviewerUid;
  const hasRecorded =
    template.peerReviewedAt != null && template.peerReviewedAt.length > 0;

  // [Edit-as-new-draft] Certified templates are immutable per the
  // lifecycle FSM, so "edit" cloned this content into a fresh draft.
  // The new draft gets a client-generated id (any non-empty string is
  // valid per SubmitDraftBody on the server) plus the same metadata,
  // and the user lands on TemplateEdit to refine before submitting.
  //
  // Capability gates:
  //   - L1 templates → the L1 platform library
  //   - L2 templates → the caller's own L2 tenant library
  // The mutations themselves enforce this server-side; we hide each CTA
  // when the caller lacks the capability for this layer to avoid 403s.
  // Clone (edit-as-new-draft) is template:author; tag is template:tag —
  // a persona may hold one without the other, so they gate separately.
  const isL1 = template.layerSource === "L1";
  const l2TenantMatch =
    !isL1 &&
    template.tenantId !== null &&
    session?.studioId !== null &&
    session?.studioId !== undefined &&
    `tenant-${session.studioId}` === template.tenantId;
  const canClone = isL1
    ? sessionCan(session, "template:author", { layer: "L1" })
    : l2TenantMatch && sessionCan(session, "template:author", { layer: "L2" });
  const canTag = isL1
    ? sessionCan(session, "template:tag", { layer: "L1" })
    : l2TenantMatch && sessionCan(session, "template:tag", { layer: "L2" });

  // [S-802] Whether this template FILLS ITS OWN SLOTS when a draft is built on
  // it: an approved set is what drafting looks for, and a stale one (its text
  // moved since it was tagged) is refused at fill time. Shown here because
  // nothing else says whether a certified template has been through review.
  const approvedVars = useTemplateVariablesApproved(
    canTag ? { templateId: template.id, tenantId: template.tenantId } : null
  ).data?.approved;

  const cloner = useCloneTemplateAsDraft();

  const handleClone = (): void => {
    if (!canClone || cloner.isPending) return;
    cloner.clone(template, (newId) =>
      navigate(`/library/${encodeURIComponent(newId)}/edit`)
    );
  };

  return (
    <SectionCard
      className="td__attest"
      title="Quick actions"
      testId="td-section-actions"
    >
      <Button
        variant="primary"
        style={{ width: "100%", marginBottom: 8 }}
        onClick={onReview}
      >
        Review contract against this template
      </Button>
      {canClone && (
        <Button
          variant="secondary"
          style={{ width: "100%", marginBottom: 8 }}
          onClick={handleClone}
          disabled={cloner.isPending}
          data-testid="td-edit-as-new-draft"
        >
          {cloner.isPending ? "Cloning…" : "Edit as new draft"}
        </Button>
      )}
      {/* S-216 — retag any template (draft / certified / re_certified /
          under_review / deprecated). Tags are metadata, so the
          lifecycle state stays put. Gated on template:tag for the
          template's layer — separate from clone (template:author), so a
          tag-only persona sees this without the clone CTA. */}
      {canTag && (
        <Button
          variant="secondary"
          style={{ width: "100%", marginBottom: 8 }}
          onClick={() => setRetagOpen(true)}
          data-testid="td-edit-tags"
        >
          Edit tags / auto-tag
        </Button>
      )}
      {/* [template-variables] The blanks and per-deal values a draft fills in —
          NOT the taxonomy tags above. Same template:tag gate. */}
      {canTag && (
        <Button
          variant="secondary"
          style={{ width: "100%", marginBottom: 8 }}
          onClick={() =>
            navigate(`/library/${encodeURIComponent(template.id)}/variables`)
          }
          data-testid="td-review-variables"
        >
          Review variables
        </Button>
      )}
      {canTag && (
        <p className="td__variables-status" data-testid="td-variables-status">
          {approvedVars === undefined || approvedVars === null
            ? "No variables approved — drafts on this template are written by the model."
            : approvedVars.stale
              ? `${approvedVars.slots.length} variables approved, but the template text has changed since — re-run and approve again.`
              : `${approvedVars.slots.length} variables approved — drafts fill them in place.`}
        </p>
      )}
      <Button
        variant="secondary"
        style={{ width: "100%", marginBottom: 8 }}
        onClick={() =>
          exportTemplate.mutate({
            templateId: template.id,
            tenantId: template.tenantId,
          })
        }
        disabled={isExporting}
        data-testid="td-export-docx"
      >
        {isExporting ? "Preparing DOCX…" : "Download as DOCX"}
      </Button>
      {peerEligibleState && isAssignedPeer && !hasRecorded && (
        <Button
          variant="secondary"
          style={{ width: "100%", marginBottom: 8 }}
          onClick={() =>
            recordPeer.mutate({
              templateId: template.id,
              tenantId: template.tenantId,
            })
          }
          disabled={recordPeer.isPending}
          data-testid="td-record-peer-review"
        >
          {recordPeer.isPending ? "Recording…" : "Record peer review"}
        </Button>
      )}
      {peerEligibleState &&
        !isAssignedPeer &&
        sessionCan(session, "template:certify", { layer: isL1 ? "L1" : "L2" }) && (
        <Button
          variant="secondary"
          style={{ width: "100%", marginBottom: 8 }}
          onClick={() => setAssigningPeer(true)}
          data-testid="td-assign-peer"
        >
          {template.peerReviewerUid != null && template.peerReviewerUid.length > 0
            ? "Re-assign peer reviewer"
            : "Assign peer reviewer"}
        </Button>
      )}
      <Button
        variant="ghost"
        style={{ width: "100%" }}
        onClick={() => {
          void navigator.clipboard?.writeText(template.id);
        }}
      >
        Copy template ID
      </Button>
      {recordPeer.isError && (
        <div className="td__attest__err" role="alert">
          Record failed — {recordPeer.error?.message ?? "unknown error"}
        </div>
      )}
      {cloner.error !== null && (
        <div className="td__attest__err" role="alert">
          Clone failed — {cloner.error}
        </div>
      )}
      {exportError !== null && (
        <div className="td__attest__err" role="alert">
          Export failed — {(exportError as { message?: string }).message ?? "unknown error"}
        </div>
      )}
      {/* Dialog (Radix) portals its content to document.body, so nesting
          these here doesn't put them under .td__side's subtree — they stay
          visible even if the panel gets collapsed while one is open. */}
      <AssignPeerReviewerModal
        template={assigningPeer ? template : null}
        callerUid={session?.uid ?? ""}
        onClose={() => setAssigningPeer(false)}
      />
      <EditTagsModal
        template={retagOpen ? template : null}
        onClose={() => setRetagOpen(false)}
      />
    </SectionCard>
  );
}

function prettyTitle(t: CertifiedTemplateRow): string {
  // S-216 — persisted human title takes precedence; legacy rows fall
  // back to the guild/dealType/tier derived label so nothing breaks
  // before backfill.
  if (typeof t.title === "string" && t.title.trim().length > 0) {
    return t.title;
  }
  return `${prettyGuild(t.guild)} ${prettyDealType(t.dealType)} — ${prettyTier(t.tier)}`;
}

function prettyGuild(g: CertifiedTemplateRow["guild"]): string {
  return g === "SAG" ? "SAG-AFTRA" : g;
}

function prettyDealType(d: CertifiedTemplateRow["dealType"]): string {
  return d.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

function prettyTier(t: CertifiedTemplateRow["tier"]): string {
  return talentTierLabel(t);
}

// [S-633 follow-up] Distinguishes a short-form deal memo from the long-form
// contract it promotes to — without this, opening a template from a chat
// source or the library gives no way to tell which one you're looking at.
// "Deal memo" (not "Short form") to avoid colliding with the UNRELATED
// dealType option also labeled "Short form" on the Templates library filter.
function prettyDocumentForm(d: CertifiedTemplateRow["documentForm"]): string {
  if (d === "deal_memo") return "Deal memo";
  if (d === "long_form") return "Long form";
  return "Form untagged";
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// ────────────────────────────────────────────────────────────────────────
// EditableDocument — wraps the read-only TokenizedDocument with an
// optional inline editor (CKEditor via TemplateEditor). Edit toggle is
// hidden for users without the role+layer claim that lets them clone
// the template into a new draft. The detail page only ever renders
// certified / re_certified templates (the L1/L2 invariant in
// useTemplateDetail rejects drafts and other states), so the only save
// path is clone-as-new-draft: it carries the edited content forward
// and navigates the user to the new draft's /edit URL so they can keep
// refining before certifying.
// ────────────────────────────────────────────────────────────────────────
function EditableDocument({
  template,
  tenantId: _tenantId,
}: {
  template: CertifiedTemplateRow;
  tenantId: string;
}): JSX.Element {
  const navigate = useNavigate();
  const session = useSession();
  const [editing, setEditing] = useState(false);
  const [marksVisible, setMarksVisible] = useState(true);
  const [draftContent, setDraftContent] = useState<string>(template.content);

  // [S-802] Which gaps a deal fills, shown in both views. The marking is
  // display only: `withoutVariableMarks` runs before anything is saved.
  const marks = useVariableMarks(template);

  // Resync when the underlying template id changes (route navigation)
  // or when the cached row rehydrates with new content.
  useEffect(() => {
    setDraftContent(template.content);
    setEditing(false);
  }, [template.id, template.content]);

  // [S-802] The editable copy is marked ONCE, when editing starts — not from an
  // effect. An effect that marks re-runs whenever the slots' identity changes
  // (the proposal query refetches on focus, and clicking into the editor is a
  // focus), and swapping the editor's `value` mid-edit throws away what the
  // author was doing. Marking at the moment Edit is clicked cannot race.
  // Marking walks the whole document, and this component re-renders on every
  // query refetch — so the read-only copy is computed once per (content, slots).
  const markedForReading = useMemo(
    () => marks.mark(template.content),
    [marks, template.content]
  );

  const startEditing = (): void => {
    // [S-802 follow-up] The EDITING copy carries marker boundaries, not the
    // read-only `<span class="tv-mark">` markup: the editor turns them into
    // markers that move with the author's edit, so the save below can say
    // where each variable ended up instead of the clone starting with none.
    setDraftContent(marks.markForEditing(template.content));
    setEditing(true);
  };

  const isL1 = template.layerSource === "L1";
  const l2TenantMatch =
    !isL1 &&
    template.tenantId !== null &&
    session?.studioId !== null &&
    session?.studioId !== undefined &&
    `tenant-${session.studioId}` === template.tenantId;
  const canEdit = isL1
    ? sessionCan(session, "template:author", { layer: "L1" })
    : l2TenantMatch && sessionCan(session, "template:author", { layer: "L2" });

  const cloner = useCloneTemplateAsDraft();

  const handleSave = (): void => {
    if (draftContent.trim().length === 0 || cloner.isPending) return;
    // The markers, read back against the document about to be stored. The
    // clone is a NEW template, so the set is carried to the new id (server
    // side, as this author's proposal — a copy nobody has reviewed yet).
    const read = slotsFromMarkerBoundaries(draftContent);
    cloner.clone(
      template,
      (newId, saved) =>
        navigate(`/library/${encodeURIComponent(newId)}/edit`, {
          // The author lands in the editor for the new draft; the news about
          // their variables travels with them rather than being lost to the
          // navigation.
          state: { variablesNote: describeVariablesOutcome(saved) },
        }),
      {
        // The clone stores the DOCUMENT; nothing of the marking is written.
        content: withoutVariableMarks(read.html),
        ...(marks.slots.length === 0
          ? {}
          : {
              variables: {
                target: marks.source,
                fromTemplateId: template.id,
                slots: read.slots.map((v) => ({ id: v.id, start: v.start, end: v.end })),
              },
            }),
      }
    );
  };

  const handleCancel = (): void => {
    setDraftContent(template.content);
    setEditing(false);
  };

  if (!editing) {
    // [S-802] The toolbar earns its place for the toggle alone: a reader who
    // cannot edit this template still wants to know which gaps a deal fills,
    // and to be able to read the contract without them.
    const hasMarks = marks.slots.length > 0;
    return (
      <>
        {(canEdit || hasMarks) && (
          <div className="td__doc__toolbar" data-testid="td-doc-toolbar">
            {hasMarks && (
              <Button
                variant="ghost"
                onClick={() => setMarksVisible((on) => !on)}
                aria-pressed={marksVisible}
                title="Show or hide the variables a deal fills"
                data-testid="td-variables-toggle"
              >
                {marksVisible ? "Hide variables" : "Show variables"}
              </Button>
            )}
            {canEdit && (
              <Button
                variant="secondary"
                className="td__doc__edit-btn"
                onClick={startEditing}
                data-testid="td-edit-inline"
              >
                ✎ Edit
              </Button>
            )}
          </div>
        )}
        {/* Marked either way — hiding is presentation, so nothing is re-parsed
            and the document underneath never changes. */}
        <div className={marksVisible ? "td__doc-vars" : "td__doc-vars cs-variables-hidden"}>
          <TokenizedDocument content={markedForReading} />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="td__doc__toolbar" data-testid="td-doc-toolbar">
        <Button
          variant="ghost"
          onClick={handleCancel}
          disabled={cloner.isPending}
          data-testid="td-edit-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={cloner.isPending || draftContent.trim().length === 0}
          data-testid="td-edit-save-inline"
        >
          {cloner.isPending ? "Cloning…" : "Save as new draft"}
        </Button>
      </div>
      {/* Same document frame as every other CKEditor surface: sticky
          toolbar, editable is the single scroller, pager in the footer.
          Bounded (not viewport-filling) because the detail page keeps its
          right rail and scrolls within .main__body. */}
      <EditorFrame
        bounded
        value={draftContent}
        onChange={setDraftContent}
        placeholder="Template content"
        ariaLabel="Template content editor"
        pagerMeta="Draft copy · unsaved changes"
      />
      <div className="td__doc__hint">
        Saving creates a new <em>draft</em> copy. The original certified
        template is unchanged.
      </div>
      {cloner.error !== null && (
        <div className="td__attest__err" role="alert">
          Clone failed — {cloner.error}
        </div>
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────
// [S-215] Tokenized document body. Renders the document through the shared
// PaginatedDocument widget (components/DocPreview.tsx) — the same read-only
// viewer the Drafts/Precedents record drawer uses — and adds the one thing
// that's specific to certified templates: a right rail surfacing every
// {{variable}} placeholder across the full document.
// ────────────────────────────────────────────────────────────────────────

const TOKEN_RE = /\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g;

function TokenizedDocument({ content }: { content: string }): JSX.Element {
  const tokens = useMemo<string[]>(() => {
    const seen = new Set<string>();
    let m: RegExpExecArray | null;
    const re = new RegExp(TOKEN_RE.source, "g");
    while ((m = re.exec(content)) !== null) {
      const name = m[1];
      if (typeof name === "string") seen.add(name);
    }
    return Array.from(seen).sort();
  }, [content]);

  const body = <PaginatedDocument content={content} testId="td-doc" />;

  if (tokens.length === 0) {
    return body;
  }

  return (
    <div className="td__doc-wrap" data-testid="td-doc-wrap">
      {body}
      <aside className="td__tokens" aria-label="Template variables">
        <div className="td__tokens__lbl">
          Variables <span className="td__tokens__ct">{tokens.length}</span>
        </div>
        <ul className="td__tokens__list">
          {tokens.map((t) => (
            <li key={t} className="td__tokens__item">
              <code>{`{{${t}}}`}</code>
            </li>
          ))}
        </ul>
        <div className="td__tokens__hint">
          These placeholders get filled in at draft time during contract
          authoring.
        </div>
      </aside>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// [S-212] Successor diff section — when this template has a `successorId`
// (i.e. it was deprecated and replaced), fetch the successor and render
// a word-level diff so users can see exactly what changed across the
// version boundary. Collapsed by default to keep the page calm; expand
// to reveal.
//
// Scope note: we only diff against the successor, not arbitrary version
// pairs. A richer "compare any two versions" view requires a backend
// version index that doesn't yet exist (CertifiedTemplateRow has
// `successorId` but no reverse `predecessorId` link).
// ────────────────────────────────────────────────────────────────────────

function SuccessorDiffSection({
  base,
  successorId,
  tenantId,
}: {
  base: CertifiedTemplateRow;
  successorId: string;
  tenantId: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const successorQuery = useAdminTemplate(open ? successorId : undefined, tenantId);

  const changes = useMemo<Change[] | null>(() => {
    if (!open || successorQuery.data === undefined) return null;
    return diffWordsWithSpace(base.content, successorQuery.data.content);
  }, [open, base.content, successorQuery.data]);

  return (
    <div className="td__diff" data-testid="td-diff">
      <Button
        variant="bare"
        className="td__diff__head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="td__diff__title">
          Compare with successor version
        </span>
        <span className="td__diff__chev" aria-hidden="true">
          {open ? "▾" : "▸"}
        </span>
      </Button>
      {open && (
        <div className="td__diff__body">
          {successorQuery.isLoading && (
            <LoadingState label="Loading successor…" />
          )}
          {successorQuery.isError && (
            <Alert label="Couldn't load successor" tone="error">
              {successorQuery.error?.message ?? "unknown error"}
            </Alert>
          )}
          {changes !== null && <DiffView changes={changes} />}
        </div>
      )}
    </div>
  );
}

function DiffView({ changes }: { changes: Change[] }): JSX.Element {
  const stats = useMemo(() => {
    let added = 0;
    let removed = 0;
    for (const c of changes) {
      if (c.added === true) added += c.value.length;
      else if (c.removed === true) removed += c.value.length;
    }
    return { added, removed };
  }, [changes]);

  return (
    <>
      <div className="td__diff__stats">
        <span className="td__diff__stat td__diff__stat--add">
          +{stats.added.toLocaleString()} chars
        </span>
        <span className="td__diff__stat td__diff__stat--rem">
          −{stats.removed.toLocaleString()} chars
        </span>
      </div>
      <pre className="td__diff__pre">
        {changes.map((c, i) => {
          if (c.added === true) {
            return (
              <ins className="td__diff__ins" key={i}>
                {c.value}
              </ins>
            );
          }
          if (c.removed === true) {
            return (
              <del className="td__diff__del" key={i}>
                {c.value}
              </del>
            );
          }
          return <span key={i}>{c.value}</span>;
        })}
      </pre>
    </>
  );
}

